import { LoadingButton } from "@mui/lab"
import { Box, Typography } from "@mui/material"
import { styled } from "@mui/system"
import { SessionState } from "app-constants/constants"
import AspectRatio from "components/common/AspectRatio"
import useJoinSession from "hooks/sessions/useJoinSession"
import useScheduleSession from "hooks/sessions/useScheduleSession"
import useSessionCopyText from "hooks/sessions/useSessionCopyText"
import useSessionState from "hooks/sessions/useSessionState"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import { pxToRem } from "utils/helpers"

interface SessionHeaderProps {
  session: ConventionSession
  scheduleSession: ReturnType<typeof useScheduleSession>
  joinSession: ReturnType<typeof useJoinSession>
  sessionState: ReturnType<typeof useSessionState>
}

const SessionHeader = (props: SessionHeaderProps) => {
  const { session, scheduleSession, joinSession, sessionState } = props
  const sessionCopyText = useSessionCopyText()

  return (
    <_Container>
      <_ImageContainerWrapper>
        <_ImageContainer ratio={437 / 250}>
          {[SessionState.READY_TO_JOIN, SessionState.JOINED].includes(sessionState) && (
            <_LiveBadge aria-label="The session is live">
              <Typography variant="paragraphAllCaps" color="gray.lightest">
                Live
              </Typography>
            </_LiveBadge>
          )}
          <_Image src={session.promotional_image_url} alt={`The header image of ${session.name}`} />
        </_ImageContainer>
      </_ImageContainerWrapper>
      <_ContentContainer>
        <Typography variant="paragraphAllCaps">
          {session.convention_exhibitor?.exhibitor_name}
        </Typography>
        <Typography variant="header1" component="h2">
          {session.name}
        </Typography>
        <_RegisterStateContainer>
          <Typography color="gray.darker" variant="paragraphItalic">
            {sessionState === SessionState.AT_CAPACITY && (
              <>This {sessionCopyText?.singular} is at capacity</>
            )}

            {sessionState === SessionState.ENDED && (
              <>Registration for this {sessionCopyText?.singular} has ended</>
            )}
          </Typography>
          {[SessionState.READY_TO_JOIN, SessionState.JOINED].includes(sessionState) ? (
            <RegisterButton variant="contained" onClick={joinSession.join}>
              Join this {sessionCopyText?.singular}
            </RegisterButton>
          ) : (
            <RegisterButton
              disabled={
                sessionState === SessionState.ENDED ||
                (!scheduleSession.isScheduled && sessionState === SessionState.AT_CAPACITY)
              }
              loading={scheduleSession.isLoading}
              variant="contained"
              onClick={
                scheduleSession.isScheduled
                  ? scheduleSession.undoSchedule
                  : scheduleSession.schedule
              }>
              {scheduleSession.isScheduled ? "Cancel registration" : "Register now"}
            </RegisterButton>
          )}
        </_RegisterStateContainer>
      </_ContentContainer>
    </_Container>
  )
}

export default SessionHeader

const _Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexFlow: "column nowrap",
  gap: pxToRem(40),
  minHeight: pxToRem(290),

  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
})) as typeof Box

const _ImageContainerWrapper = styled(Box)(() => ({
  flex: 1,
})) as typeof Box

const _ImageContainer = styled(AspectRatio)(() => ({
  flex: 1,
  overflow: "hidden",
  borderRadius: pxToRem(10),
})) as typeof AspectRatio

const _Image = styled("img")(() => ({
  display: 'block',
  maxHeight: '100%',
  maxWidth: '100%'
}))

const _ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexFlow: "column nowrap",
  alignItems: "flex-start",
  gap: pxToRem(30),

  [theme.breakpoints.up("md")]: {
    padding: `${pxToRem(20)} 0`,
  },
})) as typeof Box

const _RegisterStateContainer = styled(Box)(() => ({
  display: "flex",
  flexFlow: "column nowrap",
  alignItems: "flex-start",
  gap: pxToRem(8),
})) as typeof Box

const RegisterButton = styled(LoadingButton)(() => ({
  width: "100%",
  maxWidth: pxToRem(280),
  height: pxToRem(50),
}))

const _LiveBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: pxToRem(20),
  left: pxToRem(28),
  borderRadius: pxToRem(100),
  backgroundColor: theme.palette.primary.main,
  padding: `${pxToRem(6)} ${pxToRem(14)}`,
})) as typeof Box
